/**
 * The smith's stall (M5-01) — barter, and no currency anywhere in it.
 *
 * There is no silver in Cwm Twrch and there is not going to be. A price is how
 * much *stuff* it takes, and what stuff is worth is a table in content
 * (`catalogue.value`): hides, meat, tusks and honey are what a smith wants,
 * because those are what a valley of hunters actually has. That single decision
 * is what makes the economy a loop rather than a shop — hunt, trade, better
 * steel, bigger hunt — and it is why the mail shirt is priced where it is: it is
 * the campaign's aspirational object, and reaching it should take a group of
 * people several weeks of good hunting.
 *
 * What you buy goes **straight onto the body**, not into the satchel. That
 * follows `interact.ts`'s looting rule and for the same reason: there is no
 * equipping in the MVP, so a weapon is either in your hands or it is not, and a
 * spear bought while already holding an axe would be a spear with nowhere to
 * live. Armour stacks, because wearing a helm and a byrnie is two decisions and
 * both of them are yes.
 *
 * Repair is the other half and it is the smaller half deliberately: `blunted`
 * costs damage (M2-04's condition-lite), and the fix is a man with a whetstone
 * and a morning. It is also why a whetstone is on the stall — a party that has
 * bought one never has to walk home to swing properly again.
 */
import { addItem, removeItem } from './item.js';
/* -------------------------------------------------------------------------- *
 * What a bad winter does to a price (M5-02)
 * -------------------------------------------------------------------------- */
/**
 * How many animals the fold holds when the village is whole.
 *
 * The denominator of the scarcity below, and it is a *constant* rather than a
 * high-water mark held in world state, deliberately: prices should track "how
 * far below a working village is this" rather than "how much worse is this than
 * the best day you ever had", which would ratchet and never come back.
 */
export const PEN_WHOLE = 4;
/**
 * What the smith charges when the wolves have been at the herd.
 *
 * A village down to two animals is a village with less to trade away, and a
 * smith with less coming in wants more for what he has. Half again at worst —
 * enough that a player who let the raids go unanswered can *feel* the byrnie
 * receding, and not so much that a bad fortnight makes the campaign goal
 * unreachable.
 *
 * This is the whole of M5-02's "pen losses propagate to barter values". It is
 * derived from the census every time it is asked rather than written into the
 * stall, so it comes back down on its own if the village ever recovers — and
 * there is no second number anywhere that could disagree with how many animals
 * are actually standing in the fold.
 */
export const SCARCITY_MAX = 1.5;
export function scarcity(penCount) {
    if (penCount >= PEN_WHOLE)
        return 1;
    const missing = Math.max(0, PEN_WHOLE - penCount) / PEN_WHOLE;
    return 1 + (SCARCITY_MAX - 1) * missing;
}
/** What this ware costs today, given how the village is doing. */
export function priceOf(ware, penCount) {
    return Math.ceil(ware.price * scarcity(penCount));
}
/**
 * What a thing is worth to somebody who trades, or 0 for anything nobody wants.
 *
 * Asked of the catalogue rather than of a tag, unlike the Meddyg's payment
 * rule: she takes *anything her shelves need* and one yarrow is as good as
 * another, where a smith is pricing goods against each other and has to be able
 * to say that a boar tusk is worth twenty rabbits. A tag query cannot say that.
 */
export function valueOf(catalogue, itemId) {
    const value = catalogue.value?.(itemId) ?? 0;
    return Number.isFinite(value) && value > 0 ? value : 0;
}
/** Everything in this satchel that anybody would take, worth most last. */
function tradeable(catalogue, inventory) {
    const goods = inventory.items
        .filter((stack) => stack.count > 0 && valueOf(catalogue, stack.defId) > 0)
        .map((stack) => ({
        defId: stack.defId,
        count: stack.count,
        value: valueOf(catalogue, stack.defId),
    }));
    // Cheapest first, so paying for a whetstone spends the rabbit meat and leaves
    // the boar's tusks in the bag. Nobody hands over the good thing when the
    // cheap thing would have covered it, and a trade system that made them would
    // teach players to empty their satchels before talking to anybody.
    return goods.sort((a, b) => a.value - b.value || (a.defId < b.defId ? -1 : 1));
}
/** What this satchel is worth all told, in barter values. */
export function purse(catalogue, inventory) {
    if (!inventory)
        return 0;
    let total = 0;
    for (const stack of tradeable(catalogue, inventory))
        total += stack.value * stack.count;
    return total;
}
/**
 * Take `price` worth of goods off somebody, cheapest first. `false` — and
 * nothing taken — if they cannot cover it.
 *
 * Change is not given, because there is nothing to give it in. Paying for a
 * three-value whetstone with a five-value hide costs the hide, which is what
 * barter is; the cheapest-first ordering is what keeps that from being a trap.
 */
export function takeGoods(catalogue, inventory, price) {
    if (price <= 0)
        return true;
    if (!inventory)
        return false;
    if (purse(catalogue, inventory) < price)
        return false;
    let owed = price;
    for (const stack of tradeable(catalogue, inventory)) {
        while (owed > 0 && stack.count > 0) {
            if (removeItem(inventory, stack.defId, 1) !== 1)
                break;
            stack.count -= 1;
            owed -= stack.value;
        }
        if (owed <= 0)
            break;
    }
    return owed <= 0;
}
/** The ware with this id on this stall, or `null` — including "no stall at all". */
export function wareOn(stall, wareId) {
    return stall?.wares.find((ware) => ware.id === wareId) ?? null;
}
/** Does the catalogue actually have the thing this ware names? */
export function wareExists(catalogue, ware) {
    switch (ware.kind) {
        case 'weapon':
            return catalogue.weapon(ware.id) !== null;
        case 'armour':
            return catalogue.armour(ware.id) !== null;
        default:
            return (catalogue.item?.(ware.id) ?? null) !== null;
    }
}
/** What the seller calls it. The catalogue's name, never a second copy. */
export function wareName(catalogue, ware) {
    switch (ware.kind) {
        case 'weapon':
            return catalogue.weapon(ware.id)?.name ?? ware.id;
        case 'armour':
            return catalogue.armour(ware.id)?.name ?? ware.id;
        default:
            return catalogue.item?.(ware.id)?.name ?? ware.id;
    }
}
/**
 * The stall, read out.
 *
 * Prose rather than a table on the wire, because this is a man telling you what
 * he has. The client draws its own menu from the same content (the stall is
 * `VillagerDef` data, which the client can read), so this line is the thing a
 * player who never opens a menu still hears.
 */
export function readStall(catalogue, def, penCount = PEN_WHOLE) {
    const wares = (def.stall?.wares ?? []).filter((ware) => wareExists(catalogue, ware));
    if (wares.length === 0)
        return 'Nothing today.';
    return wares
        .map((ware) => `${wareName(catalogue, ware)} (${priceOf(ware, penCount)})`)
        .join(', ');
}
/**
 * Why this purchase cannot happen, or `null`.
 *
 * Split out from the act so the same sentence can answer a client asking "may
 * I" and a server saying "no" — one rule, one wording, one place to change it.
 */
export function buyFault(catalogue, def, buyer, inventory, wareId, penCount = PEN_WHOLE) {
    const ware = wareOn(def.stall, wareId);
    if (!ware || !wareExists(catalogue, ware))
        return 'no-sale';
    if (purse(catalogue, inventory) < priceOf(ware, penCount))
        return 'cannot-afford';
    // A weapon has one place to go and it may be occupied. Said *before* payment
    // rather than after, because a smith who took the hides and then handed back
    // a spear you cannot hold would be a bug wearing a rule's clothes.
    if (ware.kind === 'weapon' && buyer && !handsFree(catalogue, buyer))
        return 'hands-full';
    return null;
}
/**
 * Empty-handed, or holding nothing better than the catalogue's default.
 *
 * The same test `interact.ts` applies to a looted weapon, and deliberately the
 * same: swapping your seax for something else is a decision the game should make
 * you take deliberately, and there is nowhere to *carry* a second weapon until
 * the item system grows one.
 */
export function handsFree(catalogue, combatant) {
    const held = combatant.loadout.weaponId;
    return held.length === 0 || held === catalogue.defaultLoadout?.weaponId;
}
/**
 * Hand the goods over and put the thing where it goes. Assumes `buyFault`
 * already said yes.
 */
export function giveWare(catalogue, combatant, inventory, ware, tick, penCount = PEN_WHOLE) {
    if (!takeGoods(catalogue, inventory, priceOf(ware, penCount)))
        return false;
    switch (ware.kind) {
        case 'weapon':
            if (combatant)
                combatant.loadout = { ...combatant.loadout, weaponId: ware.id, edge: 'keen' };
            return true;
        case 'armour':
            if (combatant && !combatant.loadout.armourIds.includes(ware.id)) {
                combatant.loadout = {
                    ...combatant.loadout,
                    armourIds: [...combatant.loadout.armourIds, ware.id],
                };
            }
            return true;
        default: {
            const def = catalogue.item?.(ware.id);
            if (inventory && def) {
                addItem(inventory, def.id, 1, tick, def.stackable, undefined, def.shelfLife !== null);
            }
            return true;
        }
    }
}
/* -------------------------------------------------------------------------- *
 * Putting an edge back on
 * -------------------------------------------------------------------------- */
/** Why the blade cannot be mended, or `null`. */
export function repairFault(catalogue, def, combatant, inventory) {
    const price = def.stall?.repairPrice;
    if (price === null || price === undefined)
        return 'no-sale';
    if (!combatant)
        return 'nothing-to-mend';
    const weapon = catalogue.weapon(combatant.loadout.weaponId);
    // A staff has no edge to lose and a man's hands have no edge to put back.
    if (!weapon?.takesEdge)
        return 'nothing-to-mend';
    if ((combatant.loadout.edge ?? 'keen') === 'keen')
        return 'nothing-to-mend';
    if (purse(catalogue, inventory) < price)
        return 'cannot-afford';
    return null;
}
/** Take the price and hand the blade back sharp. Assumes `repairFault` said yes. */
export function mend(catalogue, def, combatant, inventory) {
    const price = def.stall?.repairPrice ?? 0;
    if (!takeGoods(catalogue, inventory, price))
        return false;
    combatant.loadout = { ...combatant.loadout, edge: 'keen' };
    return true;
}
//# sourceMappingURL=barter.js.map